import React, { createContext, ReactNode, useContext } from 'react';
import ApiClient from '../../lib/api-client';
import { IPaginatedResultDTO } from '@common-frontend-submodule/interfaces/common/ipaginated-result.interface';
import { IUserStatusDTO } from '@common-frontend-submodule/interfaces/user-status/iuser-status.interface';
import { IUserStatusCreationDTO } from '@common-frontend-submodule/interfaces/user-status/iuser-status-creation.interface';
import { IUserStatusUpdateDTO } from '@common-frontend-submodule/interfaces/user-status/iuser-status-update.interface';
import { IGetUserStatusesRequestDTO } from '@common-frontend-submodule/interfaces/user-status/iget-user-statuses-request.interface';
import { IApiResponseGenericDTO } from '@common-frontend-submodule/interfaces/common/iapi-response.interface';
import { UserStatusRoutes } from '@common-frontend-submodule/enums/routes/user-statuses-routes.enum';

interface UserStatusContextProps {
  getById: (userStatusId: string) => Promise<IApiResponseGenericDTO<IUserStatusDTO>>;
  filter: (model: IGetUserStatusesRequestDTO) => Promise<IApiResponseGenericDTO<IPaginatedResultDTO<IUserStatusDTO>>>;
  create: (model: IUserStatusCreationDTO) => Promise<IApiResponseGenericDTO<IUserStatusDTO>>;
  update: (userStatusId: string, model: IUserStatusUpdateDTO) => Promise<IApiResponseGenericDTO<IUserStatusDTO>>;
  delete: (userStatusId: string) => Promise<IApiResponseGenericDTO<boolean>>;
}

const UserStatusContext = createContext<UserStatusContextProps | undefined>(undefined);

const apiClient = ApiClient.getInstance();

export const UserStatusProvider: React.FC<{ children: ReactNode }> = ({ children }) =>  {
  const getById = async (userStatusId: string): Promise<IApiResponseGenericDTO<IUserStatusDTO>> => {
    return await apiClient.getRequest<IUserStatusDTO>(`${UserStatusRoutes.BasePrefix}/${userStatusId}`);
  };

  const filter = async (model: IGetUserStatusesRequestDTO): Promise<IApiResponseGenericDTO<IPaginatedResultDTO<IUserStatusDTO>>> => {
    return await apiClient.postRequest<IGetUserStatusesRequestDTO, IPaginatedResultDTO<IUserStatusDTO>>(
      `${UserStatusRoutes.BasePrefix}/${UserStatusRoutes.List}`,
      model
    );
  };

  const create = async (model: IUserStatusCreationDTO): Promise<IApiResponseGenericDTO<IUserStatusDTO>> => {
    return await apiClient.postRequest<IUserStatusCreationDTO, IUserStatusDTO>(UserStatusRoutes.BasePrefix, model);
  };

  const update = async (userStatusId: string, model: IUserStatusUpdateDTO): Promise<IApiResponseGenericDTO<IUserStatusDTO>> => {
    return await apiClient.putRequest<IUserStatusUpdateDTO, IUserStatusDTO>(`${UserStatusRoutes.BasePrefix}/${userStatusId}`, model);
  };

  const deleteUserStatus = async (userStatusId: string): Promise<IApiResponseGenericDTO<boolean>> => {
    return await apiClient.deleteRequest<null, boolean>(`${UserStatusRoutes.BasePrefix}/${userStatusId}`, null, { userStatusId });
  };

  return (
    <UserStatusContext.Provider value={{ getById, filter, create, update, delete: deleteUserStatus }}>
      {children}
    </UserStatusContext.Provider>
  );
};

export const useUserStatusStore = () => {
  const context = useContext(UserStatusContext);
  if (!context) {
    throw new Error('useUserStatusStore must be used within a UserStatusProvider');
  }
  return context;
};
